import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Field — the label, description and error around a control, and the wiring between them.
 *
 * This is the piece that is easy to get wrong by hand and expensive when it is wrong: the label
 * has to point at the control, the description and the error have to reach it through
 * `aria-describedby`, and an invalid control has to say so with `aria-invalid` rather than only
 * turning red (WCAG 1.3.1, 3.3.1, 1.4.1). Field generates the ids and hands the control everything
 * it needs, so no caller has to remember.
 *
 *   <Field label="Station name" error={error}>{(control) => <Input {...control} />}</Field>
 */
import { useId } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { AlertIcon } from './icons.js';
/** The same props without `labelId`, for a control that spreads them straight onto a DOM element. */
export function controlDomProps(control) {
    const { labelId: _labelId, ...rest } = control;
    return rest;
}
export function Field({ label, description, error, required, showOptional = false, as = 'field', className, children, }) {
    const t = useUiText();
    const id = useId();
    const controlId = `${id}-control`;
    const labelId = `${id}-label`;
    const descriptionId = description ? `${id}-description` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
    const control = {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
        labelId,
    };
    const labelContent = (_jsxs(_Fragment, { children: [label, required ? null : showOptional ? (_jsx("span", { className: "clav-field__optional", children: t('optional') })) : null] }));
    const body = (_jsxs(_Fragment, { children: [as === 'group' ? (_jsx("legend", { className: "clav-field__label", id: labelId, children: labelContent })) : (_jsx("label", { className: "clav-field__label", id: labelId, htmlFor: controlId, children: labelContent })), children(control), description ? (_jsx("p", { className: "clav-field__description", id: descriptionId, children: description })) : null, error ? (_jsxs("p", { className: "clav-field__error", id: errorId, children: [_jsx(AlertIcon, {}), _jsxs("span", { className: "clav-visually-hidden", children: [t('field.describedByError'), ": "] }), error] })) : null] }));
    return as === 'group' ? (_jsx("fieldset", { className: cx('clav-field', className), children: body })) : (_jsx("div", { className: cx('clav-field', className), children: body }));
}
//# sourceMappingURL=field.js.map